import React from "react";

/**
 * AppErrorBoundary
 *
 * Top-level safety net so the application never silently fails to a
 * blank white screen.  If anything inside the React tree throws during
 * render, we catch it here and present a calm recovery card with a
 * one-click escape hatch to /reset.html (the static rescue page that
 * unregisters the service worker, purges caches, and reloads).
 *
 * This is intentionally a CLASS component because class-based
 * componentDidCatch / getDerivedStateFromError is still the only way
 * to catch React render errors. There is no hook equivalent.
 */
export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message:
        (error && (error.message || String(error))) ||
        "Something didn't load correctly.",
    };
  }

  componentDidCatch(error, info) {
    // Keep a breadcrumb in the console so we can diagnose post-mortem
    // without surfacing a stack trace to clients.
    // eslint-disable-next-line no-console
    console.error("[AppErrorBoundary] render error:", error, info);
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch (_e) {
      /* noop */
    }
  };

  handleReset = () => {
    // The static rescue page handles SW unregister + cache purge + reload.
    window.location.href = "/reset.html";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        data-testid="app-error-boundary"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#0d0c0a",
          color: "#f5f1ea",
          padding: "24px",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
        }}
      >
        <div style={{ maxWidth: 460, width: "100%" }}>
          <p
            style={{
              fontSize: 10,
              letterSpacing: "0.25em",
              textTransform: "uppercase",
              color: "#c9a96e",
              margin: 0,
              marginBottom: 12,
            }}
          >
            VÉRAULT · Member Suite
          </p>
          <h1
            style={{
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: 32,
              lineHeight: 1.15,
              margin: 0,
              marginBottom: 16,
            }}
          >
            One moment — something didn't load.
          </h1>
          <p
            style={{
              fontStyle: "italic",
              fontSize: 14,
              lineHeight: 1.6,
              color: "#b7a98a",
              margin: 0,
              marginBottom: 28,
            }}
          >
            This is almost always fixed by refreshing. If the page keeps
            looking blank, the "Clear cache &amp; retry" button will
            unregister stale assets and bring everything back.
          </p>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="app-error-reload"
              style={{
                flex: "1 1 160px",
                padding: "12px 18px",
                background: "#c9a96e",
                color: "#0d0c0a",
                fontSize: 11,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                border: 0,
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={this.handleReset}
              data-testid="app-error-reset"
              style={{
                flex: "1 1 160px",
                padding: "12px 18px",
                background: "transparent",
                color: "#f5f1ea",
                fontSize: 11,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                border: "1px solid #3a342a",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              Clear cache &amp; retry
            </button>
          </div>

          {this.state.message ? (
            <p
              style={{
                marginTop: 32,
                fontSize: 11,
                color: "#5a5346",
                wordBreak: "break-word",
              }}
            >
              <span style={{ opacity: 0.7 }}>diagnostic · </span>
              {this.state.message}
            </p>
          ) : null}
        </div>
      </div>
    );
  }
}
